import { useEffect, useMemo, useRef, useState } from "react";
import { Building2, ChartNoAxesCombined, Layers3, ListFilter, MapPinned } from "lucide-react";
import BrandHeader from "../components/BrandHeader";
import CompanyCard from "../components/CompanyCard";
import IndiaGrantMap from "../components/IndiaGrantMap";
import ProjectRecordsTable from "../components/ProjectRecordsTable";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { downloadProjectsCsv, getCompanies, getFilters, getOverview, getProjects } from "../lib/api";
import { normalizeGeographyLabel } from "../lib/display";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/633ce1c8-ea35-4260-9b22-7c74713b9b3b/images/b7c832efd4c491c1b0a6fee091e71efc520b3d3b1b6e442835c47ff9be691176.png";

const defaultFilters = {
  company: "",
  company_id: "",
  state: "all",
  district: "all",
  sector: "all",
  year: "all",
  implementation_mode: "all",
};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function ExplorerPage() {
  const [overview, setOverview] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [records, setRecords] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [page, setPage] = useState(1);
  const [recordsPage, setRecordsPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [exportingRecords, setExportingRecords] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [resultsMode, setResultsMode] = useState("records");
  const [sortBy, setSortBy] = useState("impact");
  const [error, setError] = useState("");
  const resultsSectionRef = useRef(null);
  const appliedQuery = "";

  const pageSize = 12;
  const recordsPageSize = 100;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (filters.company === appliedFilters.company && filters.company_id === appliedFilters.company_id) return;
      setAppliedFilters((current) => ({ ...current, company: filters.company, company_id: filters.company_id }));
      setPage(1);
      setRecordsPage(1);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [appliedFilters.company, appliedFilters.company_id, filters.company, filters.company_id]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [overviewData, filterData] = await Promise.all([getOverview(), getFilters()]);
        setOverview(overviewData);
        setFilterOptions(filterData);
      } catch (loadError) {
        setError("The explorer is getting things ready. Please refresh in a moment.");
      } finally {
        setBootstrapping(false);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    const loadCompanies = async () => {
      setLoading(true);
      setError("");

      try {
        const params = {
          page,
          page_size: pageSize,
          sort_by: sortBy,
          ...(appliedFilters.company ? { company: appliedFilters.company } : {}),
          ...(appliedFilters.company_id ? { company_id: appliedFilters.company_id } : {}),
          ...(appliedFilters.state !== "all" ? { state: appliedFilters.state } : {}),
          ...(appliedFilters.district !== "all" ? { district: appliedFilters.district } : {}),
          ...(appliedFilters.sector !== "all" ? { sector: appliedFilters.sector } : {}),
          ...(appliedFilters.year !== "all" ? { year: appliedFilters.year } : {}),
          ...(appliedFilters.implementation_mode !== "all" ? { implementation_mode: appliedFilters.implementation_mode } : {}),
        };
        const response = await getCompanies(params);
        setCompanies(response.items);
        setTotal(response.total);
      } catch (loadError) {
        setError("We couldn't load CSR matches right now. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, [appliedFilters, page, sortBy]);

  useEffect(() => {
    const loadRecords = async () => {
      setRecordsLoading(true);
      try {
        const params = {
          page: recordsPage,
          page_size: recordsPageSize,
          ...(appliedFilters.company ? { company: appliedFilters.company } : {}),
          ...(appliedFilters.company_id ? { company_id: appliedFilters.company_id } : {}),
          ...(appliedFilters.state !== "all" ? { state: appliedFilters.state } : {}),
          ...(appliedFilters.district !== "all" ? { district: appliedFilters.district } : {}),
          ...(appliedFilters.sector !== "all" ? { sector: appliedFilters.sector } : {}),
          ...(appliedFilters.year !== "all" ? { year: appliedFilters.year } : {}),
          ...(appliedFilters.implementation_mode !== "all" ? { implementation_mode: appliedFilters.implementation_mode } : {}),
        };
        const response = await getProjects(params);
        setRecords(response.items);
        setRecordsTotal(response.total);
      } catch (loadError) {
        setError("We couldn't load row-level project records right now. Please try again.");
      } finally {
        setRecordsLoading(false);
      }
    };

    loadRecords();
  }, [appliedFilters, recordsPage]);

  const activeFilterCount = useMemo(
    () => Object.entries(appliedFilters).filter(([key, value]) => key !== "company_id" && value && value !== "all").length,
    [appliedFilters],
  );

  const appliedOnlyFilterCount = useMemo(
    () => Object.entries(appliedFilters).filter(([key, value]) => key !== "company_id" && value && value !== "all").length,
    [appliedFilters],
  );

  const isFilteredResults = activeFilterCount > 0;
  const effectiveViewMode = isFilteredResults ? "list" : viewMode;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const buildSelectedRecordsHref = (company) => {
    const params = new URLSearchParams();
    if (appliedFilters.company) params.set("company", appliedFilters.company);
    if (appliedFilters.company_id) params.set("company_id", appliedFilters.company_id);
    if (appliedFilters.state !== "all") params.set("state", appliedFilters.state);
    if (appliedFilters.district !== "all") params.set("district", appliedFilters.district);
    if (appliedFilters.sector !== "all") params.set("sector", appliedFilters.sector);
    if (appliedFilters.year !== "all") params.set("year", appliedFilters.year);
    if (appliedFilters.implementation_mode !== "all") params.set("implementation_mode", appliedFilters.implementation_mode);
    params.set("companyName", company.company_name);
    params.set("cin", company.cin);
    return `/company/${company.id}/selected-records?${params.toString()}`;
  };

  const highlightCards = overview
    ? [
        { label: "CSR companies", value: formatNumber(overview.company_count), icon: Building2 },
        { label: "Project records", value: formatNumber(overview.project_count), icon: ChartNoAxesCombined },
        { label: "Districts covered", value: formatNumber(overview.district_count), icon: MapPinned },
        { label: "Sectors mapped", value: formatNumber(overview.sector_count), icon: Layers3 },
      ]
    : [];

  const applyMapFilters = (nextFilters) => {
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setPage(1);
    setRecordsPage(1);
  };

  const handleMapFilterChange = (key, value) => {
    applyMapFilters({
      ...filters,
      [key]: value,
    });
  };

  const handleCompanyFilterInputChange = (value) => {
    setFilters((current) => ({
      ...current,
      company: value,
      company_id: "",
    }));
  };

  const handleCompanySuggestionSelect = (suggestion) => {
    applyMapFilters({
      ...filters,
      company: suggestion.company_name,
      company_id: suggestion.company_id,
    });
  };

  const handleResetMapFilters = () => {
    applyMapFilters({
      ...filters,
      company: "",
      company_id: "",
      sector: "all",
      year: "all",
      implementation_mode: "all",
    });
  };

  const handleMapStateSelect = (stateValue) => {
    applyMapFilters({
      ...filters,
      state: stateValue,
      district: "all",
    });
  };

  const handleMapDistrictSelect = (districtValue) => {
    applyMapFilters({
      ...filters,
      district: districtValue,
    });
  };

  const handleClearMapDistrict = () => {
    applyMapFilters({
      ...filters,
      district: "all",
    });
  };

  const handleClearMapGeography = () => {
    applyMapFilters({
      ...filters,
      state: "all",
      district: "all",
    });
  };

  const handleShowAllRecords = () => {
    setResultsMode("records");
    resultsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleExportAllRecords = async () => {
    setExportingRecords(true);
    try {
      const blob = await downloadProjectsCsv({
        ...(appliedFilters.company ? { company: appliedFilters.company } : {}),
        ...(appliedFilters.company_id ? { company_id: appliedFilters.company_id } : {}),
        ...(appliedFilters.state !== "all" ? { state: appliedFilters.state } : {}),
        ...(appliedFilters.district !== "all" ? { district: appliedFilters.district } : {}),
        ...(appliedFilters.sector !== "all" ? { sector: appliedFilters.sector } : {}),
        ...(appliedFilters.year !== "all" ? { year: appliedFilters.year } : {}),
        ...(appliedFilters.implementation_mode !== "all" ? { implementation_mode: appliedFilters.implementation_mode } : {}),
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "csr-all-records.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      setError("We couldn't export the current record set right now. Please try again.");
    } finally {
      setExportingRecords(false);
    }
  };

  return (
    <div className="site-shell min-h-screen">
      <BrandHeader />

      <main className="mx-auto flex w-full max-w-[1700px] flex-col gap-10 px-4 pb-20 pt-8 sm:px-6 lg:px-8 lg:gap-14 lg:pt-12">
        <section
          className="hero-surface section-frame stagger-in grid gap-8 p-6 md:grid-cols-[1.5fr_1fr] md:p-10 lg:p-12"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          data-testid="hero-section"
        >
          <div className="space-y-7">
            <div className="hero-line" />
            <div className="space-y-4">
              <h1 className="max-w-4xl text-4xl font-black leading-none tracking-tight text-slate-950 sm:text-5xl lg:text-6xl" data-testid="hero-heading">
                Find CSR grants by state, district, project type, and spend.
              </h1>
              <p className="max-w-2xl text-sm font-medium leading-relaxed text-slate-600 sm:text-base" data-testid="hero-description">
                Explore a cleaner CSR master dataset sourced from MCA filings, with company identifiers, district-level coverage, implementation modes, and project-level spend signals — all structured for fast NGO discovery.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {highlightCards.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card
                    key={item.label}
                    className={`rounded-none border-slate-200 bg-white/90 ${index > 0 ? `stagger-delay-${Math.min(index, 3)}` : ""}`}
                    data-testid={`overview-card-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <CardContent className="flex items-center justify-between gap-4 p-4">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-slate-500">{item.label}</p>
                        <p className="mt-2 text-2xl font-black text-slate-950">{item.value}</p>
                      </div>
                      <div className="border border-slate-200 bg-slate-50 p-3 text-[#F26522]">
                        <Icon size={20} />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          <div className="section-frame flex flex-col gap-5 bg-white/92 p-6" data-testid="funding-landscape-panel">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">Funding landscape</p>
              <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-950">Top districts where CSR spend is landing.</h2>
            </div>

            <div className="space-y-3">
              {(overview?.top_districts || []).slice(0, 4).map((district) => (
                <div key={district.label} className="flex items-center justify-between border-b border-slate-200 pb-3" data-testid={`top-district-${district.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div>
                    <p className="font-semibold text-slate-900">{normalizeGeographyLabel(district.label)}</p>
                    <p className="text-sm text-slate-500">{formatNumber(district.project_count)} mapped projects</p>
                  </div>
                  <p className="text-lg font-extrabold text-[#1E3A8A]">₹{formatNumber(district.total_spend_crore)} Cr</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <IndiaGrantMap
          appliedQuery={appliedQuery}
          filters={filters}
          appliedFilters={appliedFilters}
          filterOptions={filterOptions}
          activeFilterCount={activeFilterCount}
          bootstrapping={bootstrapping}
          onCompanyInputChange={handleCompanyFilterInputChange}
          onCompanySuggestionSelect={handleCompanySuggestionSelect}
          onApplyState={handleMapStateSelect}
          onApplyDistrict={handleMapDistrictSelect}
          onFilterChange={handleMapFilterChange}
          onResetFilters={handleResetMapFilters}
          onClearDistrict={handleClearMapDistrict}
          onClearGeography={handleClearMapGeography}
          onShowAllRecords={handleShowAllRecords}
        />

        <section ref={resultsSectionRef} className="section-frame stagger-in stagger-delay-2 p-6 md:p-8" data-testid="results-section">
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">Search results</p>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-950" data-testid="results-count">
                {resultsMode === "companies"
                  ? loading
                    ? "Loading matches..."
                    : `${total.toLocaleString("en-IN")} CSR companies found`
                  : recordsLoading
                    ? "Loading matching rows..."
                    : `${recordsTotal.toLocaleString("en-IN")} matching records found`}
              </h2>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Tabs value={resultsMode} onValueChange={setResultsMode} className="w-full sm:w-auto">
                <TabsList className="grid w-full grid-cols-2 rounded-none bg-slate-100 p-1 sm:w-[420px]" data-testid="results-mode-tabs">
                  <TabsTrigger value="records" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-slate-950" data-testid="results-mode-records-tab">
                    All records
                  </TabsTrigger>
                  <TabsTrigger value="companies" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-slate-950" data-testid="results-mode-companies-tab">
                    Aggregated by company
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {resultsMode === "companies" ? (
                <>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-full rounded-none border-slate-300 sm:w-[220px]" data-testid="sort-by-select">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="impact">Highest total spend</SelectItem>
                      <SelectItem value="coverage">Widest geographic coverage</SelectItem>
                      <SelectItem value="projects">Most project records</SelectItem>
                      <SelectItem value="outlay">Highest total outlay</SelectItem>
                      <SelectItem value="alphabetical">Alphabetical</SelectItem>
                    </SelectContent>
                  </Select>

                  {isFilteredResults ? (
                    <div className="border border-slate-300 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-600" data-testid="filtered-list-mode-note">
                      Filtered results are shown in list view for easier scanning.
                    </div>
                  ) : (
                    <div className="flex border border-slate-300 bg-white">
                      <Button
                        type="button"
                        variant="ghost"
                        className={`rounded-none px-4 ${viewMode === "grid" ? "bg-slate-950 text-white hover:bg-slate-900 hover:text-white" : "text-slate-700"}`}
                        onClick={() => setViewMode("grid")}
                        data-testid="grid-view-button"
                      >
                        Grid
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        className={`rounded-none px-4 ${viewMode === "list" ? "bg-slate-950 text-white hover:bg-slate-900 hover:text-white" : "text-slate-700"}`}
                        onClick={() => setViewMode("list")}
                        data-testid="list-view-button"
                      >
                        List
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="border border-slate-300 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-600" data-testid="all-records-mode-note">
                    All records shows every matching source row while preserving your search criteria.
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-none border-slate-300"
                    onClick={handleExportAllRecords}
                    disabled={exportingRecords || recordsLoading}
                    data-testid="all-records-export-button"
                  >
                    {exportingRecords ? "Exporting…" : "Export CSV"}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {error ? (
            <div className="mt-6 border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="results-error-message">
              {error}
            </div>
          ) : null}

          {resultsMode === "companies" ? (
            <>
              <div className={`mt-6 ${effectiveViewMode === "grid" ? "grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" : "flex flex-col gap-4"}`}>
                {companies.map((company) => (
                  <CompanyCard
                    key={company.id}
                    company={{
                      ...company,
                      selectedRecordsHref: appliedOnlyFilterCount > 0 ? buildSelectedRecordsHref(company) : null,
                    }}
                    viewMode={effectiveViewMode}
                  />
                ))}
              </div>

              {!loading && companies.length === 0 ? (
                <div className="mt-6 border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600" data-testid="empty-state">
                  Try widening your filters or searching by CIN, project, district, sector, implementation mode, or spend range.
                </div>
              ) : null}

              <div className="mt-8 flex flex-col gap-4 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="active-filter-summary">
                  <ListFilter size={16} />
                  {activeFilterCount} active search signals applied
                </div>

                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-none"
                    disabled={page === 1}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                    data-testid="pagination-previous-button"
                  >
                    Previous
                  </Button>
                  <span className="text-sm font-semibold text-slate-700" data-testid="pagination-indicator">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    type="button"
                    className="rounded-none bg-[#F26522] text-white hover:bg-[#D9561B]"
                    disabled={page >= totalPages}
                    onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                    data-testid="pagination-next-button"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="mt-6" data-testid="all-records-panel">
              <ProjectRecordsTable
                records={records}
                loading={recordsLoading}
                page={recordsPage}
                pageSize={recordsPageSize}
                total={recordsTotal}
                onPageChange={setRecordsPage}
                emptyMessage="No project rows match the current criteria."
                hideSectorColumn={appliedFilters.sector !== "all"}
              />
            </div>
          )}
        </section>
      </main>
    </div>
  );
}